import React, { useEffect, useState } from "react"
import { IconMapPin } from "@tabler/icons-react"
import { ILocation } from "@dc-ireland/shared"
import apiClient from "../../lib/api"
import LandingEmptyState from "./LandingEmptyState"
import LandingLocationCard from "./LandingLocationCard"
import LandingCarousel from "./LandingCarousel"
import LandingReveal from "./LandingReveal"
import LandingSectionHeader from "./LandingSectionHeader"

const toMapUrl = (address: string[]) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.join(", "))}`

const LandingLocationsSection: React.FC = () => {
  const [locations, setLocations] = useState<ILocation[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const data = await apiClient.getLocations()
        setLocations(data)
      } catch (err) {
        console.error("Error fetching locations:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchLocations()
  }, [])

  return (
    <section id="our-locations" className="relative scroll-mt-24 overflow-hidden bg-[#fafafa] py-20">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_100%_50%,rgba(214,229,255,0.4),transparent_55%)]" />

      <div className="relative mx-auto flex max-w-6xl flex-col items-center px-4 md:px-8 lg:px-20">
        <LandingSectionHeader
          eyebrow="Find us near you"
          title="Our Locations"
          description="Find a chapter near you and join us for worship, fellowship, and spiritual growth. We'd love to welcome you."
        />
      </div>

      {loading ? (
        <div className="relative mt-14 flex justify-center py-12">
          <span className="loading loading-spinner loading-lg text-dc-primary-900" />
        </div>
      ) : locations.length === 0 ? (
        <div className="relative mt-14">
          <LandingEmptyState
            pattern="map"
            icon={IconMapPin}
            title="New chapters on the horizon"
            description="We're mapping out gathering places across Ireland. Check back soon — we'd love to welcome you when a location opens near you."
            action={
              <a
                href="mailto:info@dominioncity.ie"
                className="inline-flex items-center justify-center rounded-lg border border-dc-primary-900/20 px-5 py-2.5 font-dm-sans text-sm font-semibold tracking-wide text-dc-primary-900 transition hover:border-dc-primary-900/40 hover:bg-dc-primary-900/5"
              >
                Get in touch
              </a>
            }
          />
        </div>
      ) : (
        <LandingReveal delay={0.1} className="relative mx-auto mt-14 max-w-6xl px-4 md:px-8 lg:px-20">
          <LandingCarousel ariaLabel="Church locations carousel">
            {locations.map(({ slug, name, address, times, pagePath }) => (
              <div
                key={slug}
                className="shrink-0 snap-start basis-[85%] sm:basis-[340px] md:basis-[360px]"
              >
                <LandingLocationCard
                  name={name}
                  address={address.join(", ")}
                  serviceTimes={times}
                  mapUrl={toMapUrl(address)}
                  pagePath={pagePath}
                />
              </div>
            ))}
          </LandingCarousel>
        </LandingReveal>
      )}
    </section>
  )
}

export default LandingLocationsSection
